import { useState } from 'react';
import { Link } from 'wouter';
import { api, type Drafter, type PromptSegment } from '../lib/api';
import type { ConfirmRequest } from '../components/Confirm';
import { useResource } from '../lib/use-resource';
import { ageOf, whenLocal } from '../lib/time';
import { costLine } from '../lib/knowledge';

/**
 * Below the Drafter instructions (fix-57): the whole system prompt as the next draft receives it, built live by the
 * desk — only the instructions and the three always-on articles change it, each edited as an article — with the tools
 * and the body check's rules; beside it, the model. Changing the model restarts the Classes counter.
 */
export function DrafterPanel({ onConfirm }: { onConfirm: (r: ConfirmRequest) => void }) {
  const d = useResource<Drafter>(() => api.drafter.get());
  if (!d.data) return <p className="list-empty">{d.error?.message ?? 'Reading the prompt…'}</p>;
  return (
    <div className="drafter-grid">
      <PromptView d={d.data} />
      <ModelCard d={d.data} onConfirm={onConfirm} onChanged={() => d.refetch()} />
    </div>
  );
}

const KIND_NOTE: Record<PromptSegment['kind'], string> = {
  instructions: 'edited above',
  article: 'an article in every draft',
  code: 'fixed in code',
};

function PromptView({ d }: { d: Drafter }) {
  return (
    <section className="drafter-prompt">
      <div className="panel-head">
        <h2>The whole prompt</h2>
        <span className="mono panel-note">{d.prompt_chars.toLocaleString()} characters · read-only</span>
      </div>
      <ol className="drafter-segments">
        {d.segments.map((s, i) => (
          <li key={i} className={`drafter-segment is-${s.kind}`}>
            <details>
              <summary>
                <span>{s.label}</span>
                <span className="mono panel-note">
                  {s.kind === 'article' && s.article_id
                    ? <Link href={`/knowledge/${s.article_id}`}>{KIND_NOTE.article}</Link>
                    : KIND_NOTE[s.kind]}
                </span>
              </summary>
              <pre className="drafter-text">{s.text}</pre>
            </details>
          </li>
        ))}
        <li className="drafter-segment is-code">
          <details>
            <summary><span>Tools offered</span><span className="mono panel-note">{d.tools.map((t) => t.name).join(', ')}</span></summary>
            <pre className="drafter-text">{JSON.stringify(d.tools, null, 2)}</pre>
          </details>
        </li>
        <li className="drafter-segment is-code">
          <details>
            <summary><span>The body check</span><span className="mono panel-note">run on every draft before you see it</span></summary>
            <ul className="drafter-rules">
              {d.body_rules.map((r) => (
                <li key={r.rule}>
                  <span className={`knowledge-badge ${r.action === 'flagged' ? 'is-muted' : ''}`}>{r.action}</span> {r.rule}
                  {r.pinned && <div className="mono panel-note">held by the line &ldquo;{r.pinned}&rdquo;</div>}
                </li>
              ))}
            </ul>
          </details>
        </li>
      </ol>
    </section>
  );
}

function ModelCard({ d, onConfirm, onChanged }: { d: Drafter; onConfirm: (r: ConfirmRequest) => void; onChanged: () => void }) {
  const m = d.model;
  const [pick, setPick] = useState(m.draft ?? '');
  const [error, setError] = useState<string | null>(null);
  const change = () => onConfirm({
    title: `Draft with ${pick}?`,
    detail: `Every draft from now on is written by ${pick} instead of ${m.draft}. The Classes screen's counter restarts `
      + 'from now, and the next draft writes a new cache entry. Read the model\'s breaking changes on Anthropic\'s model '
      + 'page first, and watch the first draft.',
    confirmLabel: 'Change the model',
    onConfirm: async () => {
      try { await api.drafter.setModel(pick); setError(null); onChanged(); } catch (e) { setError((e as Error).message); }
    },
  });
  return (
    <section className="drafter-model">
      <div className="panel-head"><h2>Model</h2></div>
      <div className="drafter-model-now">{m.draft ?? 'not set'}</div>
      <p className="mono panel-note">{costLine(m)}</p>
      <p className="mono panel-note">
        Cache: {m.cache?.warm ? 'warm' : 'cold — the next draft writes it'}
        {m.cache?.last_call_at && <> · last draft call {ageOf(m.cache.last_call_at)} ago, read {m.cache.cache_read?.toLocaleString()} / wrote {m.cache.cache_write?.toLocaleString()} tokens</>}
      </p>
      {m.measure_from && <p className="mono panel-note">on this model since {whenLocal(m.measure_from)}</p>}
      <label className="knowledge-field">
        <span className="mono">Change to</span>
        <select value={pick} onChange={(e) => setPick(e.target.value)}>
          {m.choices.map((c) => <option key={c.id} value={c.id}>{c.id} — ${c.in} / ${c.out} per M</option>)}
        </select>
      </label>
      <button type="button" className="btn-primary" disabled={!pick || pick === m.draft} onClick={change}>Change…</button>
      {error && <p className="list-error">{error}</p>}
      <p className="mono panel-note">Triage: {m.triage ?? 'not set'} (not changed here)</p>
    </section>
  );
}
